/**
 * PrintPreviewDialog
 *
 * A reusable compound-component Dialog designed for print preview flows.
 * It injects print base styles when open, provides a title bar and action
 * buttons that are automatically hidden on print (`.noPrint`), and wraps
 * children in a `#printRoot` container with safe-area padding.
 *
 * Usage:
 * ```tsx
 * <PrintPreviewDialog
 *   open={open}
 *   onClose={onClose}
 *   title="Vista previa"
 *   printStyles={myFeatureStyles}
 *   disablePrint={!hasData}
 * >
 *   <MyReportContent />
 * </PrintPreviewDialog>
 * ```
 *
 * @module shared/components/dialogs/PrintPreviewDialog
 */
import React from 'react';

import CloseRounded from '@mui/icons-material/CloseRounded';
import PrintRounded from '@mui/icons-material/PrintRounded';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';

import printBaseStyles from './printBaseStyles';

/* ───────────────────── Types ───────────────────── */

export interface PrintPreviewDialogProps {
  /** Controls dialog visibility */
  open: boolean;
  /** Called when the dialog should close */
  onClose: () => void;
  /** Title shown in the dialog header (hidden on print) */
  title?: string;
  /** Additional @media print CSS appended after base styles */
  printStyles?: string;
  /** Disables the print button (e.g. when there is no data) */
  disablePrint?: boolean;
  /** Dialog max-width */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Content to render inside the print area */
  children: React.ReactNode;
}

/* ───────────────────── Component ───────────────────── */

const PrintPreviewDialog: React.FC<PrintPreviewDialogProps> = ({
  open,
  onClose,
  title = 'Vista previa del reporte',
  printStyles = '',
  disablePrint = false,
  maxWidth = 'md',
  children,
}) => {
  const combinedStyles = printBaseStyles + printStyles;

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {open && <style>{combinedStyles}</style>}

      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={maxWidth}
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: '16px',
              overflow: 'hidden',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}>
        {/* ─── Title Bar (hidden on print) ─── */}
        <DialogTitle
          className="noPrint"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2,
            px: 3,
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            {title}
          </Typography>
          <IconButton onClick={onClose} className="noPrint" size="small" sx={{ color: 'text.secondary' }}>
            <CloseRounded />
          </IconButton>
        </DialogTitle>

        {/* ─── Content ─── */}
        <DialogContent sx={{ p: 0, overflow: 'auto', flex: '1 1 auto' }}>
          <Box id="printRoot" sx={{ p: 3 }}>
            {children}
          </Box>
        </DialogContent>

        {/* ─── Action Buttons (hidden on print) ─── */}
        <DialogActions
          className="noPrint"
          sx={{
            px: 3,
            py: 2,
            borderTop: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}>
          <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={disablePrint}
            startIcon={<PrintRounded />}
            onClick={handlePrint}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '10px',
              px: 3,
              height: 42,
            }}>
            Imprimir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PrintPreviewDialog;
